import React, { SyntheticEvent, useEffect, useRef, useState, useCallback } from "react";
import MapImage from "../components/Map";
import MyTabs from "../components/Tabs";
import {
  handle_dataset_change,
  handle_resolution_or_year_or_month_change,
  handle_page_load_comparator,
  handle_get_level_three_datasets,
  handle_compare_calculate
} from "../functions/backend_handlers";
import {
  AttributeResponse,
  CustomMetricData,
  MetadataRespone,
  ResolutionKeys,
  StateToShow,
  StorageCustomMetricData,
} from "../types";
import YearSelectSlider from "../components/Slider";
import DynamicSelector from "../components/DynamicSelector";
import MapSelector from "../components/MapSelector";
import StandaloneLegend from "../components/StandaloneLegend";
import chroma from "chroma-js";
import FeatureTable from "../components/FeatureTable";
import useSessionStorage from "../components/Session";
import WaterTypeExport from "../components/waterTypeExport";
import UserDefinedMetric from "../components/UserDefinedMetric";
import { convertToMap, createComparitorDataMessage } from "../functions/miscellaneous";

interface DatasetOption {
  value: string;
  label: string;
  category: string;
}

const Comparator: React.FC = () => {
  //user_defined_datasets
  const [available_user_datasets, setAvailableUserDatasets] = useState<StorageCustomMetricData>({});
  const [is_current_user_defined, setIsCurrentUserDefined] = useSessionStorage<boolean>("comparator_is_current_user_defined",false);

  // colorings
  const [colormap_types, setColormapTypes] = useState<string>("");
  const [colormap_bases, setColormapBases] = useState<string>("");
  const [attribute_legend_labels, setAttributeLegendLabels] = useState<string>("");

  // feature table
  const [selectedFeature, setSelectedFeature] = useSessionStorage<any>("comparator_selectedFeature", null);

  //secondary states
  const [available_resolutions, setAvailableResolutions] = useState<ResolutionKeys[]>([]);
  const [polygon_values, setPolygonValues] = useState<{ [key: string]: string; }[]>([ {}, {}, {} ]);
  const [has_time, setHasTime] = useState<boolean>(false);
  const [valid_years, setValidYears] = useState<number[]>([]);
  const [attributes_list, setAttributesList] = useState<{
    names: string[];
    titles: string[];
  }>({ names: [], titles: [] });
  
  const [available_datasets, setAvailableDatasets] = useState<{ categories: string[], names: string[]; titles: string[] }>({categories:[], names: [], titles: [] });
  const attribute_categories = {"high": 0, "medium":1, "low":2};
  const [polygon_type, setPolygonType] = useState<"Continuous" | "Discrete">("Continuous");

  //main states
  const [current_resolution, setCurrentResolution] = useState<ResolutionKeys>("state");
  const [current_dataset, setCurrentDataset] = useState<string>("");
  const [current_attribute, setCurrentAttribute] = useState<string>("");
  const [current_year, setCurrentYear] = useState<number | undefined>(0);
  const [current_month, setCurrentMonth] = useState<number | undefined>(0);
  const [current_parameters, setCurrentParameters] = useState<StateToShow>({});
  const [is_attribute_fgw, setIsAttributeFgw] = useState<boolean | null>(false);
  const [is_attribute_bgw, setIsAttributeBgw] = useState<boolean | null>(false);
  const [is_attribute_fsw, setIsAttributeFsw] = useState<boolean | null>(false);

  const [zoomLevel, setZoomLevel] = useSessionStorage<number>("comparator_zoomLevel",3.2)
  const [center, setCenter] = useSessionStorage<[number, number]>('comparator_center', [38.5, -99.3]);
  
  // Track which map is being updated to prevent circular updates
  const [updatingMapId, setUpdatingMapId] = useState<string | null>(null);
  
  // Handler for when any map changes
  const handleMapChange = useCallback((mapId: string, zoom: number, newCenter: [number, number]) => {
    setUpdatingMapId(mapId);
    setZoomLevel(zoom);
    setCenter(newCenter);
    
    // Clear the updating flag after a short delay
    setTimeout(() => {
      setUpdatingMapId(null);
    }, 50);
  }, [setZoomLevel, setCenter]);

  const updateAllAttributes = async (
    dataset: string,
    attribute: string,
    parameters: any,
    resolution: ResolutionKeys,
    year: number | undefined,
    month: number | undefined
  ) => {
    let attribute_fgw;
    try {
      attribute_fgw = await handle_resolution_or_year_or_month_change(
        dataset,
        attribute,
        parameters,
        resolution,
        year,
        month,
        "fgw",
        "False"
      );
    } catch (error) {
      attribute_fgw = undefined;
    }

    let attribute_bgw;
    try {
      attribute_bgw = await handle_resolution_or_year_or_month_change(
        dataset,
        attribute,
        parameters,
        resolution,
        year,
        month,
        "bgw",
        "False"
      );
    } catch (error) {
      attribute_bgw = undefined;
    }

    let attribute_fsw;
    try {
      attribute_fsw = await handle_resolution_or_year_or_month_change(
        dataset,
        attribute,
        parameters,
        resolution,
        year,
        month,
        "fsw",
        "False"
      );
    } catch (error) {
      attribute_fsw = undefined;
    }
    setCurrentParameters(parameters);
    updateAttribute(attribute_fgw, "fgw");
    updateAttribute(attribute_bgw, "bgw");
    updateAttribute(attribute_fsw, "fsw");
  };

  const updateMetadata = (metadata: MetadataRespone) => {
    setAvailableResolutions(metadata.Data.available_resolutions);
    setAttributesList({
      names: metadata.Data.available_attributes,
      titles: metadata.Data.available_attribute_titles,
    });
    setValidYears(metadata.Data.available_years.map((value) => Number(value)));
    setHasTime(metadata.Data.has_time);
    setCurrentDataset(metadata.Data.dataset_name);
    setCurrentAttribute(metadata.Data.attribute_name);
    setCurrentYear(Number(metadata.Data.available_years[0]));
    setCurrentMonth(Number(metadata.Data.available_months[0]));
    setColormapTypes(metadata.Data.colormap_types);
    setAttributeLegendLabels(metadata.Data.attribute_legend_labels);
  };

  const update_polygon_values = ( polygon_values : { [key: string]: string;}, polygon_water_type : number) => {
    //0 equation_fresh_surface_water
    //1 equation_fresh_ground_water
    //2 equation_brackish_ground_water
    
    if (polygon_water_type === 1) { //
      setIsAttributeFgw(true);
      setPolygonValues((prevValues) => {
        const newValues = [...prevValues];
        newValues[0] = polygon_values;
        return newValues;
      });
    } else if (polygon_water_type === 2) {
      setIsAttributeBgw(true);
      setPolygonValues((prevValues) => {
        const newValues = [...prevValues];
        newValues[1] = polygon_values;
        return newValues;
      });
    } else if (polygon_water_type === 0) {
      setIsAttributeFsw(true);
      setPolygonValues((prevValues) => {
        const newValues = [...prevValues];
        newValues[2] = polygon_values;
        return newValues;
      });
    }
  }

  const setIsAttribute = (value : boolean, polygon_water_type : number) => {
    if (polygon_water_type === 1) setIsAttributeFgw(value)
    else if (polygon_water_type === 2)setIsAttributeBgw(value);
    else if (polygon_water_type === 0) setIsAttributeFsw(value);
  }

  const updateAttribute = (
    attribute: AttributeResponse | undefined,
    water_type: string
  ) => {
    if (attribute) {
      if (water_type === "fgw") {
        setIsAttributeFgw(true);
        setPolygonValues((prevValues) => {
          const newValues = [...prevValues];
          newValues[0] = attribute.Data.polygon_values;
          return newValues;
        });
      } else if (water_type === "bgw") {
        setIsAttributeBgw(true);
        setPolygonValues((prevValues) => {
          const newValues = [...prevValues];
          newValues[1] = attribute.Data.polygon_values;
          return newValues;
        });
      } else if (water_type === "fsw") {
        setIsAttributeFsw(true);
        setPolygonValues((prevValues) => {
          const newValues = [...prevValues];
          newValues[2] = attribute.Data.polygon_values;
          return newValues;
        });
      }
      setPolygonType(attribute.Data.polygon_type);
      setCurrentResolution(attribute.Data.resolution);
    } else {
      if (water_type === "fgw") {
        setIsAttributeFgw(false);
      } else if (water_type === "bgw") {
        setIsAttributeBgw(false);
      } else if (water_type === "fsw") {
        setIsAttributeFsw(false);
      }
    }
  };

  const handleDataSetChangeOnDropdown = (option: DatasetOption | null) => {
    if(option && option.category !== "Custom Metrics"){
      setAvailableResolutions(["state", "county", "huc2", "huc4", "huc6", "huc8"])
      handleDatasetChange(option.value);
    } else if(option && option.category === "Custom Metrics") {
      handleUserDatasetChange(option.value)
    }
  };

  const customMetricResolutionChange = async (current_resolution : ResolutionKeys, user_dataset_name: string) => {

    const current_user_data = available_user_datasets[user_dataset_name];

    //set polygon values for the data
    for(let index=0; index < current_user_data.custom_attributes.length; index++){
      
      if(current_user_data.custom_attributes[index]){
        let messageData = createComparitorDataMessage(current_user_data.comparitor_data, current_resolution, index)

        if ( current_user_data.polygon_type[index] == "Continuous") {
          if(current_user_data.sliderValues[index] != null)
          messageData = { ...messageData, map_value: {type: "Continuous", is_flipped: current_user_data.isFlipped[index], map: [current_user_data.sliderValues[index][0], current_user_data.sliderValues[index][1]]}};
        } else {
          messageData = { ...messageData, map_value: {type: "Discrete", map: convertToMap(current_user_data.buckets,"bgw") }};
        }
        const resp_comp_calc = await handle_compare_calculate(messageData);

        if("results" in resp_comp_calc){
          update_polygon_values(resp_comp_calc.results, index)
        } else {
          setIsAttribute(false, index)
        }
      } else {
        setIsAttribute(false, index)
      }
    }

  }

  const handleUserDatasetChange = async (user_dataset_name: string, current_available_user_datasets? : StorageCustomMetricData) => {
    //0 equation_fresh_surface_water
    //1 equation_fresh_ground_water
    //2 equation_brackish_ground_water
    if(!current_available_user_datasets) current_available_user_datasets = available_user_datasets;

    setCurrentDataset(user_dataset_name);

    const current_user_data = current_available_user_datasets[user_dataset_name];

    //check if we have to lock resolution, then change it!
    let to_use_resolution = current_resolution;

    if(current_user_data.comparitor_data.has_user_defined){
      to_use_resolution = current_user_data.comparitor_data.resolution;
      setCurrentResolution(current_user_data.comparitor_data.resolution)
      setAvailableResolutions([current_user_data.comparitor_data.resolution])
    } else {
      setAvailableResolutions(["state", "county", "huc2", "huc4", "huc6", "huc8"])
    }

    const water_type_keys : ["fsw","fgw","bgw"] = ["fsw","fgw","bgw"];

    //set polygon values for the data
    for(let index=0; index < current_user_data.custom_attributes.length; index++){
      
      if(current_user_data.custom_attributes[index]){
        let messageData = createComparitorDataMessage(current_user_data.comparitor_data, to_use_resolution, index)

        if ( current_user_data.polygon_type[index] == "Continuous") {
          if(current_user_data.sliderValues[index] != null)
          messageData = { ...messageData, map_value: {type: "Continuous", is_flipped: current_user_data.isFlipped[index], map: [current_user_data.sliderValues[index][0], current_user_data.sliderValues[index][1]]}};
        } else {
          messageData = { ...messageData, map_value: {type: "Discrete", map: convertToMap(current_user_data.buckets, water_type_keys[index]) }};
        }
        const resp_comp_calc = await handle_compare_calculate(messageData);

        if("results" in resp_comp_calc){
          update_polygon_values(resp_comp_calc.results, index)
        } else {
          setIsAttribute(false, index)
        }
      } else {
        setIsAttribute(false, index)
      }
    }
   
    setIsCurrentUserDefined(true);
  }

  const handleDatasetChange = async (dataset: string) => {
    setCurrentDataset(dataset);
    let metadata, attribute_fgw, attribute_bgw, attribute_fsw, current_parameters;
    try {
      ({
        metadata,
        attribute: attribute_fgw,
        current_parameters,
      } = await handle_dataset_change(dataset, current_resolution, "fgw"));
    } catch (error) {
      console.error("Error fetching attribute_fgw:", error);
      attribute_fgw = undefined;
    }
    try {
      ({ attribute: attribute_bgw } = await handle_dataset_change(
        dataset,
        current_resolution,
        "bgw"
      ));
    } catch (error) {
      console.error("Error fetching attribute_bgw:", error);
      attribute_bgw = undefined;
    }
    try {
      ({ attribute: attribute_fsw } = await handle_dataset_change(
        dataset,
        current_resolution,
        "fsw"
      ));
    } catch (error) {
      console.error("Error fetching attribute_fsw:", error);
      attribute_fsw = undefined;
    }
    if (!current_parameters || !metadata) {
      throw new Error("Error fetching metadata or current_parameters");
    }
    setCurrentParameters(current_parameters);
    updateMetadata(metadata);
    updateAttribute(attribute_fgw, "fgw");
    updateAttribute(attribute_bgw, "bgw");
    updateAttribute(attribute_fsw, "fsw");
    setIsCurrentUserDefined(false);
  };

  const handleResolutionOrYearOrMonthChange = async ({
    resolution,
    year,
    month
  }: {
    resolution?: ResolutionKeys;
    year?: number;
    month?: number;
  }) => {
    if(is_current_user_defined){
      if(resolution) {
        setCurrentResolution(resolution);
        customMetricResolutionChange(resolution, current_dataset);
      }
    } else {
      if (resolution) setCurrentResolution(resolution);
      else resolution = current_resolution;
      if (year) setCurrentYear(Number(year));
      else year = current_year;

      if (month) setCurrentMonth(Number(month));
      else month = current_month;

      updateAllAttributes(
        current_dataset,
        current_attribute,
        current_parameters,
        resolution,
        year,
        month
      );
    }
  };

  const appendToDatalistAndSetToValue = (new_data : CustomMetricData) => {

    setAvailableUserDatasets((prevDatasets) => {
      let new_metric = true;
      if(new_data.name in prevDatasets) new_metric = false;

      const updatedDatasets: StorageCustomMetricData = {
        ...prevDatasets,
        [new_data.name]: new_data,
      };

      let updatedDatalist = available_datasets;
      if(new_metric){
        updatedDatalist = {
          names: [...available_datasets.names, new_data.name],
          titles: [...available_datasets.titles, new_data.name],
          categories: [...available_datasets.categories, "Custom Metrics"]
        };
      }
      
      setAvailableDatasets(updatedDatalist);

      handleUserDatasetChange(new_data.name, updatedDatasets)

      //set dropdown selector
      setCurrentDataset(new_data.name)

      return updatedDatasets;
    });


  }

  useEffect(() => {
    const storedDatasetsString = localStorage.getItem("custom_metrics");
    const storedDatasets : StorageCustomMetricData = storedDatasetsString ? JSON.parse(storedDatasetsString) : {};
    
    setAvailableUserDatasets(storedDatasets);
    
    handle_get_level_three_datasets().then(({datalist}) => {
      // Append the keys from storedDatasets to datalist
      Object.keys(storedDatasets).forEach(key => {
        datalist.names.push(key);
        datalist.titles.push(key); // Using the same key as the title
        datalist.categories.push("Custom Metrics"); // Fixed category for all
      });
      setAvailableDatasets(datalist);

      return {datalist};
    }).catch((error) => {

      let datalist : {
        names: string[];
        titles: string[];
        categories: string[];
      } = {
        names: [],
        titles: [],
        categories: [],
      }

      
      Object.keys(storedDatasets).forEach(key => {
        datalist.names.push(key);
        datalist.titles.push(key); // Using the same key as the title
        datalist.categories.push("Custom Metrics"); // Fixed category for all
      });
      setAvailableDatasets(datalist);
      return {datalist};
    }).then(({datalist}) => {
    //if first page load!
    handle_page_load_comparator().then(({ metadata, attribute, current_parameters }) => {
      if(!metadata && !attribute && !current_parameters) {
        // set first to custom if possible
        if(datalist.categories.length > 0){
          handleUserDatasetChange(datalist.names[0], storedDatasets);
        }
        return
      }

      if (current_parameters.fgw) {
        setCurrentParameters(current_parameters.fgw);
      } else if (current_parameters.bgw) {
        setCurrentParameters(current_parameters.bgw);
      } else if (current_parameters.fsw) {
        setCurrentParameters(current_parameters.fsw);
      }


      if (metadata.fgw) updateMetadata(metadata.fgw);
      else if (metadata.bgw) updateMetadata(metadata.bgw);
      else if (metadata.fsw) updateMetadata(metadata.fsw);
      
      updateAttribute(attribute.fgw, "fgw");
      updateAttribute(attribute.bgw, "bgw");
      updateAttribute(attribute.fsw, "fsw");
      setIsCurrentUserDefined(false);
    })

    }).catch((error: any) => {

    });
  }, []);




  const handleTabChangeOnSelect = (
    event: SyntheticEvent,
    newValue: ResolutionKeys
  ) => {
    handleResolutionOrYearOrMonthChange({ resolution: newValue });
  };

  // Handle the slider change
  const handleYearSliderChange = (
    event: Event,
    newValue: number | number[]
  ) => {
    // newValue is the index of the selected year
    handleResolutionOrYearOrMonthChange({ year: valid_years[newValue as number] });
  };

  const handleDynamicSelectorChange = async (key: string, value: string) => {
    // Directly modify the current_parameters object
    const updatedParameters = { ...current_parameters };

    if (updatedParameters[key]) {
      updatedParameters[key].current_code = value;
    }
    updateAllAttributes(
      current_dataset,
      current_attribute,
      updatedParameters,
      current_resolution,
      current_year,
      current_month
    );
  };

  function getColorByValue(value: number): string {
    let colorPalette: string[];
    const colormapType = colormap_types; 
    const colormapBase = colormap_bases;

    const customColormaps: Record<string, string[]> = {
      viridis_r: ['#440154', '#482777', '#3f4a8a', '#31678e', '#26838f', '#1f9d8a', '#6cce5a', '#b6de2b', '#fee825'],
      gist_heat_r: ['#0d0887', '#3b049a', '#6300a7', '#8b0aa5', '#b12a90', '#cb4679', '#e06461', '#f1834b', '#fca636', '#fcce25'],
      Purples_r: ['#fcfbfd', '#efedf5', '#dadaeb', '#bcbddc', '#9e9ac8', '#807dba', '#6a51a3', '#54278f', '#3f007d'],
      copper_r: ['#ffc784', '#f0bc7d', '#e1b075', '#d2a56e', '#c49967', '#b68f60', '#a78359', '#997952', '#8c6e4b', '#7e6445', '#715a3e', '#644f38', '#574631', '#4c3c2b', '#3f3225', '#332a1f', '#282019', '#1d1913', '#120f0b', '#000000'],
      tab10: ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'],
      tab20: ['#7dcf4f', '#7bca4e', '#78c64c', '#76c04b', '#74bc4a', '#71b748', '#6fb147', '#6cad45', '#6aa844', '#68a343', '#659e41', '#639940', '#61953f', '#5e903d', '#5c8c3c', '#5a883b', '#578339', '#547e38', '#527936', '#507535'],
      "greys": ['#ffffff', '#f0f0f0', '#d9d9d9', '#bdbdbd', '#969696', '#737373', '#525252', '#252525', '#000000'],
      "rdylbu": ['#a50026', '#d73027', '#f46d43', '#fdae61', '#fee090', '#e0f3f8', '#abd9e9', '#74add1', '#4575b4', '#313695'],
    };

    if (customColormaps[colormapBase]) {
      // Use the custom colormap if it exists
      colorPalette = customColormaps[colormapBase];
    } else {
      colorPalette = chroma.scale(colormapBase as chroma.BrewerPaletteName).colors(20);
    }

    // Handle discrete (qualitative) colors
    if (colormapType === "qualitative") {
      const discretePalette = colorPalette;
      // Find the nearest index based on normalized value
      const index = Math.round(value * (discretePalette.length - 1));
      return discretePalette[index] || discretePalette[discretePalette.length - 1]; // Fallback in case of out-of-bounds
    }
    // Handle continuous (sequential/diverging) colormap
    return chroma.scale(colorPalette).domain([0, 1])(value).hex();
  }

  // Create a unique key that changes only when data changes, NOT when zoom/center changes
  const mapGroupKey = `${current_resolution}-${current_dataset}`;

  return (
    <div className="contact-page-container">
      <div className="contact-page-left" style={{ padding: 0 }}>
        <MyTabs
          key={current_resolution}
          selectedTab={current_resolution}
          onTabChange={handleTabChangeOnSelect}
          tabStatus={available_resolutions}
        />
        
        {/* 2x2 GRID - Full width and height */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gridTemplateRows: '1fr 1fr', 
          gap: '10px',
          width: '100%',
          height: 'calc(100% - 40px)', /* Subtract tab height */
          paddingTop: '15px',
          background: 'transparent'
        }}>
          {/* First row, first column: Brackish Groundwater */}
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            background: 'white'
          }}>
            <h3 style={{ margin: '0 0 5px 0', fontSize: '16px', fontWeight: '600', color: '#1976d2', paddingLeft: '10px' }}>Brackish Groundwater</h3> 
            <div style={{ flex: 1, position: 'relative', background: 'white' }}>
              {is_attribute_fgw ? (
                <MapImage
                  resolution={current_resolution}
                  polygon_values={polygon_values[0]}
                  min={undefined}
                  max={undefined}
                  categories={attribute_categories}
                  polygon_type={polygon_type}
                  attribute_name={current_attribute}
                  current_dataset={current_dataset}
                  colormap_bases={colormap_bases}
                  colormap_types={colormap_types}
                  attributes_list={attributes_list}
                  current_parameters={current_parameters}
                  available_datasets={available_datasets}
                  selectedOverlay={"None"}
                  attribute_legend_labels={attribute_legend_labels}
                  is_hml={true}
                  is_legend={"OFF"}
                  show_coloring={true}
                  default_zoom_level={3.2}
                  max_zoom={5.5}
                  min_zoom={3.2}
                  is_log={false}
                  is_overlay={"False"}
                  geojson_frame={undefined}
                  popup_name={{}}
                  popup_description={undefined}

                  zoomLevel = {zoomLevel}
                  setZoomLevel = {setZoomLevel}
                  center = {center}
                  setCenter = {setCenter}
                  
                  onMapChange={(zoom, newCenter) => handleMapChange('fgw', zoom, newCenter)}
                  isUpdating={updatingMapId !== null && updatingMapId !== 'fgw'}

                  key={`${mapGroupKey}-fgw`}
                />
              ) : (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: '100%',
                  background: 'white',
                  borderRadius: '4px'
                }}>
                  <p>No data available for Brackish Groundwater</p>
                </div>
              )}
            </div>
          </div>
          
          {/* First row, second column: Fresh Groundwater  */}
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            background: 'white'
          }}>
            <h3 style={{ margin: '0 0 5px 0', fontSize: '16px', fontWeight: '600', color: '#1976d2', paddingLeft: '10px' }}>Fresh Groundwater</h3>
            <div style={{ flex: 1, position: 'relative', background: 'white' }}>
              {is_attribute_bgw ? (
                <MapImage
                  resolution={current_resolution}
                  polygon_values={polygon_values[1]}
                  min={undefined}
                  max={undefined}
                  categories={attribute_categories}
                  polygon_type={polygon_type}
                  attribute_name={current_attribute}
                  current_dataset={current_dataset}
                  colormap_bases={colormap_bases}
                  colormap_types={colormap_types}
                  attributes_list={attributes_list}
                  current_parameters={current_parameters}
                  available_datasets={available_datasets}
                  selectedOverlay={"None"}
                  attribute_legend_labels={attribute_legend_labels}
                  is_hml={true}
                  is_legend={"OFF"}
                  show_coloring={true}
                  default_zoom_level={3.2}
                  max_zoom={5.5}
                  min_zoom={3.2}
                  is_log={false}
                  is_overlay={"False"}
                  geojson_frame={undefined}
                  popup_name={{}}
                  popup_description={undefined}

                  zoomLevel = {zoomLevel}
                  setZoomLevel = {setZoomLevel}
                  center = {center}
                  setCenter = {setCenter}
                  
                  onMapChange={(zoom, newCenter) => handleMapChange('bgw', zoom, newCenter)}
                  isUpdating={updatingMapId !== null && updatingMapId !== 'bgw'}

                  key={`${mapGroupKey}-bgw`}
                />
              ) : (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: '100%',
                  background: 'white',
                  borderRadius: '4px'
                }}>
                  <p>No data available for Fresh Groundwater</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Second row, first column: Fresh Surface Water */}
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            background: 'white'
          }}>
            <h3 style={{ margin: '0 0 5px 0', fontSize: '16px', fontWeight: '600', color: '#1976d2', paddingLeft: '10px' }}>Fresh Surface Water</h3>
            <div style={{ flex: 1, position: 'relative', background: 'white' }}>
              {is_attribute_fsw ? (
                <MapImage
                  resolution={current_resolution}
                  polygon_values={polygon_values[2]}
                  min={undefined}
                  max={undefined}
                  categories={attribute_categories}
                  polygon_type={polygon_type}
                  attribute_name={current_attribute}
                  current_dataset={current_dataset}
                  colormap_bases={colormap_bases}
                  colormap_types={colormap_types}
                  attributes_list={attributes_list}
                  current_parameters={current_parameters}
                  available_datasets={available_datasets}
                  selectedOverlay={"None"}
                  attribute_legend_labels={attribute_legend_labels}
                  is_hml={true}
                  is_legend={"OFF"}
                  show_coloring={true}
                  default_zoom_level={3.2}
                  max_zoom={5.5}
                  min_zoom={3.2}
                  is_log={false}
                  is_overlay={"False"}
                  geojson_frame={undefined}
                  popup_name={{}}
                  popup_description={undefined}

                  zoomLevel = {zoomLevel}
                  setZoomLevel = {setZoomLevel}
                  center = {center}
                  setCenter = {setCenter}
                  
                  onMapChange={(zoom, newCenter) => handleMapChange('fsw', zoom, newCenter)}
                  isUpdating={updatingMapId !== null && updatingMapId !== 'fsw'}

                  key={`${mapGroupKey}-fsw`}
                />
              ) : (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: '100%',
                  background: 'white',
                  borderRadius: '4px'
                }}>
                  <p>No data available for Fresh Surface Water</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Second row, second column: Legend and Feature Table */}
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            overflowX: 'auto',
            background: 'white',
            padding: '0',
            height: '100%'
          }}>
            {/* Legend at the top - styled to match Image 1 */}
            <div style={{ 
              marginBottom: '0',
              borderBottom: '1px solid #e2e8f0',
              borderWidth: "90%",
              borderRadius: '0',
              padding: '10px',
              background: 'white',
              boxShadow: 'none'
            }}>
              <StandaloneLegend
                getColor={getColorByValue}
                minValue={undefined} 
                maxValue={undefined}
                polygon_type={polygon_type}
                polygon_values={polygon_values[2]}
                current_dataset={current_dataset}
                attribute_legend_labels={"Viability"}
                categories={attribute_categories}
              />
            </div>
            
            {/* Feature Table below the legend - styled to match Image 1 */}
            <div style={{ 
              marginTop: '0px',
              flex: 1,
              borderRadius: '0',
              boxShadow: 'none',
              background: 'white'
            }}>
              <FeatureTable 
                featureData_0={polygon_values[1]} 
                featureData_1={polygon_values[0]} 
                featureData_2={polygon_values[2]} 
                key={selectedFeature} 
              />
            </div>
          </div>
        </div>
      </div>
  
      <div className="contact-page-right">  
        <div className="scrollable-content">
          <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: '600', color: '#1976d2' }}>Viability Metrics</h3>
          
          {current_dataset !== "" && (
            <div style={{ marginBottom: '15px' }}>
              <label style={{ color:"#1976d2", display: 'block', marginBottom: '8px', fontWeight: '500' }}>Select Metric</label>
              <MapSelector
                key={current_dataset}
                data={available_datasets}
                onSelect={handleDataSetChangeOnDropdown}
                baseValue={current_dataset}
                is_clearable={false}
                available_resolutions={available_resolutions}
              />
            </div>
          )}
  
          {!is_current_user_defined && Object.keys(current_parameters).map((key) => {
            const { type, titles, codes, current_code } = current_parameters[key as keyof typeof current_parameters];
            const title = key.split("_")[0];
            const selectedCategory = available_datasets.categories.find((category, index) =>
              available_datasets.names[index] === current_dataset
            );
  
            return (
              <React.Fragment key={key}>
                {selectedCategory !== "Custom Metrics" && (
                  <div style={{ marginBottom: '15px' }}>
                    <DynamicSelector
                      title={title}
                      type={type}
                      values={codes}
                      labels={titles}
                      name={key}
                      selectedValue={current_code}
                      onChange={(value) => handleDynamicSelectorChange(key, value)}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
  
          {has_time && current_year && !is_current_user_defined && (
            <div style={{ marginBottom: '15px' }}>
              <YearSelectSlider
                key={current_year}
                value={current_year}
                handleYearSliderChange={handleYearSliderChange}
                years={valid_years}
              />
            </div>
          )}
  
          {/* Action Buttons Section - FINAL CORRECTED VERSION */}
          <div style={{ marginTop: '25px' }}>
            {/* Export button at the top, right-aligned */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end',
              marginBottom: '15px'
            }}>
              {polygon_values.some(obj => Object.keys(obj).length > 0) && (
                <WaterTypeExport
                  polygon_values={polygon_values}
                  current_resolution={current_resolution}
                  current_attribute={current_attribute}
                  current_dataset={current_dataset}
                  current_parameters={current_parameters}
                  current_year={current_year}
                />
              )}
            </div>
            
            {/* Divider Line */}
            <div style={{
              height: '1px',
              backgroundColor: '#e2e8f0',
              margin: '15px 0',
              width: '100%'
            }}></div>
            
            {/* Customize Metric button at the bottom, full width */}
            <div style={{ marginTop: '15px', width: '100%' }}>
              <UserDefinedMetric 
                appendToDatalistAndSetToValue={appendToDatalistAndSetToValue}
                incoming_comparitor_data={ is_current_user_defined ? available_user_datasets[current_dataset] : undefined}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Comparator;